import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { useAuth } from "../firebase/AuthProvider";
import { toast } from 'react-toastify';
import { ValidateOtpResponseDto } from "../interfaces/ExtermalUserDtos/ExternalUsersDtos";
import EventNotification from "../interfaces/eventNotification/EventNotification";
import { useNotifications } from "./NotificationProvider";
import { MessageSendResponse } from "../interfaces/conversationDtos/conversationdto";
import { Subscription } from "rxjs";
import { currentConversationId$ } from "../services/Subjects/userSubject";
import { useNotificationState } from "./NotificationStateContext";
import { AppEvents } from '../events/events';
import { isTrainingWorkNotification, matchesTrainingNotificationContext, TrainingNotificationContext } from '../utilities/notifications/trainingWorkEvents';

/**
 * The shape of our context: a single HubConnection or null.
 */
export type ChatConnectionStatus = "disconnected" | "connecting" | "connected" | "reconnecting";

interface ChatConnectionContextType {
    connection: signalR.HubConnection | null;
    connectionStatus: ChatConnectionStatus;
    connectionVersion: number;
    resetConnection: (externalUserDetails: ValidateOtpResponseDto) => void;
}

const ChatConnectionContext = createContext<ChatConnectionContextType | null>(null);

// const ChatConnectionContext = createContext<signalR.HubConnection | null>(null);

interface ChatConnectionProviderProps {
    children: React.ReactNode;
}

export const ChatConnectionProvider: React.FC<ChatConnectionProviderProps> = ({
    children,
}) => {

    const { notifyEvent } = useNotifications();
    const { currentUser, currentUserInfo, impersonatedUserId } = useAuth();
    const trainingContextRef = useRef<TrainingNotificationContext>({});
    trainingContextRef.current = { authenticatedUserId: currentUser?.uid, employeeId: currentUserInfo?.id,
        organizationId: currentUserInfo?.organization.id, impersonatedUserId };
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<ChatConnectionStatus>("disconnected");
    // Increments after the initial connection and after every successful reconnect.
    // Consumers can use this to reconcile server-backed chat state.
    const [connectionVersion, setConnectionVersion] = useState(0);
    const [externalUser, setExternalUser] = useState<ValidateOtpResponseDto>();
    const currentConversationIdRef = useRef(0);
    const activeConnectionRef = useRef<signalR.HubConnection | null>(null);
    const activeConnectionUserIdRef = useRef<string | null>(null);
    const connectionGenerationRef = useRef(0);
    const {addNewNotification} = useNotificationState();

    // Keep provider callbacks current without making callback identity changes
    // tear down and recreate the SignalR connection.
    const notifyEventRef = useRef(notifyEvent);
    const addNewNotificationRef = useRef(addNewNotification);

    useEffect(() => {
        notifyEventRef.current = notifyEvent;
    }, [notifyEvent]);

    useEffect(() => {
        addNewNotificationRef.current = addNewNotification;
    }, [addNewNotification]);

    useEffect(() => {
        if (!externalUser && !currentUser) {
            const existingConnection = activeConnectionRef.current;

            if (existingConnection) {
                console.log("[CHAT-HUB] No authenticated user remains; stopping active connection", {
                    boundUserId: activeConnectionUserIdRef.current,
                    connectionId: existingConnection.connectionId,
                });

                activeConnectionRef.current = null;
                activeConnectionUserIdRef.current = null;
                connectionGenerationRef.current += 1;

                void existingConnection.stop().catch((error) => {
                    console.warn("[CHAT-HUB] Failed to stop connection after logout", error);
                });
            }

            setConnection(null);
            setConnectionStatus("disconnected");
            setConnectionVersion(0);
            return;
        }

        const boundUserId =
            currentUser?.uid ??
            externalUser?.externalUserDetailId?.toString() ??
            "external-user";

        const generation = ++connectionGenerationRef.current;
        let disposed = false;
        let subscription: Subscription | undefined;
        let createdConnection: signalR.HubConnection | null = null;
        const trainingNotificationIds = new Set<string>();

        console.log("[CHAT-HUB] Starting connection lifecycle", {
            boundUserId,
            generation,
            previousBoundUserId: activeConnectionUserIdRef.current,
        });

        const previousConnection = activeConnectionRef.current;
        if (previousConnection) {
            console.log("[CHAT-HUB] Replacing previous connection", {
                previousBoundUserId: activeConnectionUserIdRef.current,
                nextBoundUserId: boundUserId,
                previousConnectionId: previousConnection.connectionId,
                generation,
            });

            activeConnectionRef.current = null;
            activeConnectionUserIdRef.current = null;

            void previousConnection.stop().catch((error) => {
                console.warn("[CHAT-HUB] Failed to stop previous connection", {
                    error,
                    generation,
                });
            });
        }

        // Invalidate visible connection state immediately so consumers cannot
        // keep using the old user's connection while the new one starts.
        setConnection(null);
        setConnectionStatus("connecting");
        setConnectionVersion(0);

        subscription = currentConversationId$.subscribe({
            next: (conversationId) => {
                currentConversationIdRef.current = conversationId;
            },
        });

        const isCurrentGeneration = () =>
            !disposed && connectionGenerationRef.current === generation;

        const getNewConn = async () => {
            try {
                const hubUrlBase = `${process.env.REACT_APP_API_BASE_URL}/chatHub`;
                let hubUrl = hubUrlBase;

                if (externalUser?.tempSessionToken) {
                    hubUrl = `${hubUrlBase}?videoToken=${externalUser.tempSessionToken}&ticketId=${externalUser.ticketId}&userId=${externalUser.externalUserDetailId}`;
                }

                const newConn = new signalR.HubConnectionBuilder()
                    .withUrl(hubUrl, {
                        accessTokenFactory: async () => {
                            if (!currentUser) return "";
                            return currentUser.getIdToken(true);
                        },
                    })
                    .withAutomaticReconnect()
                    .configureLogging(signalR.LogLevel.Information)
                    .build();

                createdConnection = newConn;

                newConn.onreconnecting((error) => {
                    if (!isCurrentGeneration()) return;

                    console.warn("[CHAT-HUB] SignalR reconnecting", {
                        boundUserId,
                        generation,
                        error,
                    });
                    setConnectionStatus("reconnecting");
                });

                newConn.onreconnected((connectionId) => {
                    if (!isCurrentGeneration()) {
                        console.warn("[CHAT-HUB] Ignoring reconnect from stale connection", {
                            boundUserId,
                            generation,
                            connectionId,
                        });
                        return;
                    }

                    console.log("[CHAT-HUB] SignalR reconnected", {
                        boundUserId,
                        generation,
                        connectionId,
                    });

                    activeConnectionRef.current = newConn;
                    activeConnectionUserIdRef.current = boundUserId;
                    setConnection(newConn);
                    setConnectionStatus("connected");
                    setConnectionVersion((version) => version + 1);
                    AppEvents.emit("UPDATE_ADMIN_REQUEST_COUNT");
                });

                newConn.onclose((error) => {
                    if (!isCurrentGeneration()) return;

                    console.warn("[CHAT-HUB] SignalR connection closed", {
                        boundUserId,
                        generation,
                        error,
                    });

                    if (activeConnectionRef.current === newConn) {
                        activeConnectionRef.current = null;
                        activeConnectionUserIdRef.current = null;
                        setConnection(null);
                    }

                    setConnectionStatus("disconnected");
                });

                newConn.on(
                    "receivenotification_web",
                    (response: EventNotification) => {
                        if (!isCurrentGeneration()) return;
                        const trainingWorkChanged = isTrainingWorkNotification(response);
                        if (trainingWorkChanged) {
                            if (!matchesTrainingNotificationContext(response, trainingContextRef.current)) return;
                            // The same persisted notification can be replayed after reconnect. Refresh and
                            // toast once per ID; retain a bounded window for this authenticated connection.
                            if (trainingNotificationIds.has(response.id)) return;
                            trainingNotificationIds.add(response.id);
                            if (trainingNotificationIds.size > 500) trainingNotificationIds.delete(trainingNotificationIds.values().next().value!);
                        }

                        console.log("[TOAST] Notification event received in ChatConnectionContext:receivenotification_web", {
                            boundUserId,
                            response,
                        });

                        notifyEventRef.current(response, {
                            toastId: response.id,
                            hideProgressBar: true,
                        });

                        addNewNotificationRef.current(response);
                        if (trainingWorkChanged) {
                            AppEvents.emit('UPDATE_EMPLOYEE_TASK_COUNT');
                            if (response.type === 'External Training Credit') AppEvents.emit('UPDATE_ADMIN_REQUEST_COUNT');
                        }
                    },
                );

                newConn.on("AdminRequestChanged", (response) => {
                    if (!isCurrentGeneration()) return;
                    console.log("[Admin Requests] Change received.", response);
                    AppEvents.emit("UPDATE_ADMIN_REQUEST_COUNT");
                });

                newConn.on("SignatureRequestChanged", (response) => {
                    if (!isCurrentGeneration()) return;
                    console.log("[Signature Requests] Change received.", response);
                    AppEvents.emit("UPDATE_SIGNATURE_REQUEST_COUNT");
                });

                newConn.on(
                    "ReceiveMessageForUserASNotification",
                    (response: MessageSendResponse) => {
                        if (!isCurrentGeneration()) return;

                        console.log("[CHAT-HUB] Legacy chat notification event received", {
                            boundUserId,
                            generation,
                            response,
                        });
                    },
                );

                newConn.on("ReceiveReminderNotification", (data) => {
                    if (!isCurrentGeneration()) return;

                    console.log("[TOAST] Reminder notification received", {
                        boundUserId,
                        data,
                    });
                    const reminderKey =
                        data.id ?? `${data.title ?? "reminder"}:${data.message ?? ""}`;
                    toast.info(`${data.title}\n${data.message}`, {
                        toastId: `reminder:${reminderKey}`,
                    });
                });

                newConn.on("NewMessage", (conversation) => {
                    if (!isCurrentGeneration()) return;

                    console.log("[CHAT-HUB] NewMessage received", {
                        boundUserId,
                        generation,
                        conversation,
                    });
                });

                console.log("[CHAT-HUB] Starting SignalR connection", {
                    boundUserId,
                    generation,
                });

                await newConn.start();

                if (!isCurrentGeneration()) {
                    console.warn("[CHAT-HUB] Connection started after identity changed; stopping stale connection", {
                        boundUserId,
                        generation,
                        connectionId: newConn.connectionId,
                    });

                    await newConn.stop().catch(() => undefined);
                    return;
                }

                activeConnectionRef.current = newConn;
                activeConnectionUserIdRef.current = boundUserId;

                console.log("[CHAT-HUB] SignalR connection active", {
                    boundUserId,
                    generation,
                    connectionId: newConn.connectionId,
                });

                setConnection(newConn);
                setConnectionStatus("connected");
                setConnectionVersion(1);
                AppEvents.emit("UPDATE_ADMIN_REQUEST_COUNT");
            } catch (err) {
                if (!isCurrentGeneration()) return;

                setConnectionStatus("disconnected");
                setConnection(null);
                console.error("[CHAT-HUB] Failed to connect SignalR", {
                    boundUserId,
                    generation,
                    error: err,
                });
            }
        };

        void getNewConn();

        return () => {
            disposed = true;

            console.log("[CHAT-HUB] Cleaning up connection lifecycle", {
                boundUserId,
                generation,
                connectionId: createdConnection?.connectionId,
            });

            subscription?.unsubscribe();

            if (connectionGenerationRef.current === generation) {
                connectionGenerationRef.current += 1;
            }

            if (createdConnection) {
                if (activeConnectionRef.current === createdConnection) {
                    activeConnectionRef.current = null;
                    activeConnectionUserIdRef.current = null;
                }

                void createdConnection.stop().catch((error) => {
                    console.warn("[CHAT-HUB] Failed to stop connection during cleanup", {
                        boundUserId,
                        generation,
                        error,
                    });
                });
            }

            setConnection((existing) =>
                existing === createdConnection ? null : existing,
            );
            setConnectionStatus("disconnected");
            setConnectionVersion(0);
        };
    }, [currentUser, externalUser]);

    

    const resetConnection = (externalUserDetails: ValidateOtpResponseDto) => {
        console.log("[CHAT-HUB] External-user connection reset requested", {
            externalUserDetailId: externalUserDetails.externalUserDetailId,
        });

        setExternalUser(externalUserDetails);
    }

    return (
        <ChatConnectionContext.Provider value={{ connection, connectionStatus, connectionVersion, resetConnection }}>
            {children}

        </ChatConnectionContext.Provider>
    );
};

/**
 * A small helper hook that returns our global chat connection (or null).
 */
// export function useChatConnection(): signalR.HubConnection | null {
//   return useContext(ChatConnectionContext);
// }
export function useChatConnection(): ChatConnectionContextType {
    const context = useContext(ChatConnectionContext);
    if (!context) {
        throw new Error("useChatConnection must be used within ChatConnectionProvider");
    }
    return context;
}
